import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, AlertTriangle, Loader2, Rocket } from 'lucide-react';
import sessionService from '@/services/sessionService.ts';
import type { VendorKpiCategory } from '@/types/vendor-kpi.ts';
import { buildVendorProfileFromAverages } from '@/utils/vendorProfileBuilder.ts';
import { maestros, getMaestroName } from '@/types/maestro.ts';
import { useAuth } from '@/context/AuthContext.tsx';
import { VendorCardHero, KPIDetailGrid } from '@/components/vendor/index.ts';
import { formatDate } from '@/utils/formatDate.ts';
import { useAsyncData } from '@/hooks/useAsyncData.ts';
import { toScore99 } from '@/utils/scoreFormat.ts';
import ScoreEvolutionChart from './ScoreEvolutionChart.tsx';
import SessionDetail from './SessionDetail.tsx';
import RankingCard from './RankingCard.tsx';
import LegalNoticeBanner from '@/components/shared/LegalNoticeBanner.tsx';

export default function MyProgressTab() {
  const { t } = useTranslation();
  const { user, isSalesperson } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);

  useEffect(() => {
    const openSessionId = (location.state as { openSessionId?: string } | null)?.openSessionId;
    if (openSessionId) {
      setSelectedSessionId(openSessionId);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  const { data, loading, error } = useAsyncData(
    async () => {
      const [stats, paginated, ranking] = await Promise.all([
        sessionService.getMyStats(),
        sessionService.getMine(1, 6),
        sessionService.getMyRanking().catch(() => null),
      ]);
      return { stats, sessions: paginated.items, ranking };
    },
    [],
  );

  const stats = data?.stats;
  const sessions = data?.sessions ?? [];
  const ranking = data?.ranking ?? null;

  if (selectedSessionId) {
    return (
      <SessionDetail
        sessionId={selectedSessionId}
        onBack={() => setSelectedSessionId(null)}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-border bg-card p-8 text-center">
        <p className="text-sm text-red-500">{t('progress.errorLoading', { error })}</p>
      </div>
    );
  }

  if (!stats || stats.totalSessions === 0) {
    return (
      <div>
        <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-card py-20">
          <Rocket className="h-12 w-12 text-primary/40" />
          <h2 className="mt-4 text-lg font-semibold text-foreground">
            {t('progress.noSessionsTitle')}
          </h2>
          <p className="mt-2 max-w-sm text-center text-sm text-muted-foreground">
            {t('progress.noSessionsDesc')}
          </p>
        </div>
      </div>
    );
  }

  // Build vendor profiles from backend KPI averages
  const profile = buildVendorProfileFromAverages(stats.kpiAveragesJson);
  const teamProfile = buildVendorProfileFromAverages(stats.teamKpiAveragesJson);
  const vendorCategories: VendorKpiCategory[] = profile?.categories ?? [];
  const teamCategories: VendorKpiCategory[] = teamProfile?.categories ?? [];
  const globalScore99 = profile?.globalScore ?? 0;

  const totalHours = stats.totalDurationSeconds / 3600;

  // Build recent activity from fetched sessions
  const recentActivity = sessions.map((s) => {
    const maestro = maestros.find((m) => m.id === s.maestroId);
    return {
      id: s.id,
      date: s.completedAt ? formatDate(s.completedAt, { day: 'numeric', month: 'short', year: 'numeric' }) : '-',
      tipo: s.simulationType,
      maestroName: maestro ? getMaestroName(maestro) : s.maestroId,
      maestroColor: maestro?.accentColor ?? 'var(--primary)',
      duration: s.durationSeconds ? `${Math.round(s.durationSeconds / 60)} min` : '-',
      score: s.report?.overallScore ?? 0,
      hasReport: !!s.report,
    };
  });

  const displayName = user ? `${user.firstName} ${user.lastName}` : t('progress.myProfile');
  const initials = user ? `${user.firstName[0]}${user.lastName[0]}` : 'YO';

  return (
    <div>
      {isSalesperson && (
        <LegalNoticeBanner
          storageKey="disclaimer-salesperson-dismissed"
          messageKey="legal.salespersonTransparency"
          learnMoreLink="/data-privacy"
        />
      )}

      {/* Two-column layout: Hero card + Strengths/Improvements */}
      <div className="mb-8 grid gap-6 lg:grid-cols-[280px_1fr]">
        {/* Left: VendorCardHero + Ranking */}
        <div className="space-y-4">
          <VendorCardHero
            name={displayName}
            initials={initials}
            globalScore={globalScore99}
            categories={vendorCategories}
            totalSimulations={stats.totalSessions}
          />
          {ranking && <RankingCard ranking={ranking} />}
        </div>

        {/* Right: Strengths + Improvements */}
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="rounded-xl border border-border bg-card p-4 sm:p-6">
            <h2 className="mb-4 font-semibold text-foreground">
              {t('progress.strengths')}
            </h2>
            <ul className="space-y-2.5">
              {stats.strengths.length > 0 ? (
                stats.strengths.map((s, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
                    <span className="text-sm text-muted-foreground">{s}</span>
                  </li>
                ))
              ) : (
                <li className="text-sm text-muted-foreground">{t('progress.strengthsEmpty')}</li>
              )}
            </ul>
          </div>

          <div className="rounded-xl border border-border bg-card p-4 sm:p-6">
            <h2 className="mb-4 font-semibold text-foreground">
              {t('progress.improvements')}
            </h2>
            <ul className="space-y-2.5">
              {stats.improvements.length > 0 ? (
                stats.improvements.map((s, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" />
                    <span className="text-sm text-muted-foreground">{s}</span>
                  </li>
                ))
              ) : (
                <li className="text-sm text-muted-foreground">{t('progress.improvementsEmpty')}</li>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* KPI Detail Grid — 6 categories with team comparison */}
      <div className="mb-6">
        <h2 className="mb-4 font-semibold text-foreground">{t('progress.detailedMetrics')}</h2>
        <KPIDetailGrid categories={vendorCategories} teamCategories={teamCategories} />
      </div>

      {/* Session history */}
      {recentActivity.length > 0 && (
        <div className="mb-6 rounded-xl border border-border bg-card p-4 sm:p-6">
          <h2 className="mb-4 font-semibold text-foreground">
            {t('progress.recentActivity')}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="whitespace-nowrap pb-2.5 pr-4 font-medium text-muted-foreground">{t('progress.tableDate')}</th>
                  <th className="whitespace-nowrap pb-2.5 pr-4 font-medium text-muted-foreground">{t('progress.tableType')}</th>
                  <th className="whitespace-nowrap pb-2.5 pr-4 font-medium text-muted-foreground">{t('progress.tableMaestro')}</th>
                  <th className="whitespace-nowrap pb-2.5 pr-4 font-medium text-muted-foreground">{t('progress.tableDuration')}</th>
                  <th className="whitespace-nowrap pb-2.5 font-medium text-muted-foreground">{t('progress.tableScore')}</th>
                </tr>
              </thead>
              <tbody>
                {recentActivity.slice(0, 6).map((entry) => (
                  <tr
                    key={entry.id}
                    onClick={entry.hasReport ? () => setSelectedSessionId(entry.id) : undefined}
                    className={`border-b border-border/50 last:border-0 ${
                      entry.hasReport ? 'cursor-pointer transition-colors hover:bg-muted' : ''
                    }`}
                  >
                    <td className="whitespace-nowrap py-2.5 pr-4 text-muted-foreground">{entry.date}</td>
                    <td className="whitespace-nowrap py-2.5 pr-4 text-foreground">{entry.tipo}</td>
                    <td className="whitespace-nowrap py-2.5 pr-4">
                      <span className="font-medium" style={{ color: entry.maestroColor }}>
                        {entry.maestroName}
                      </span>
                    </td>
                    <td className="whitespace-nowrap py-2.5 pr-4 text-muted-foreground">{entry.duration}</td>
                    <td className="py-2.5">
                      <span
                        className={`font-semibold ${
                          toScore99(entry.score) >= 80
                            ? 'text-green-500'
                            : toScore99(entry.score) >= 65
                              ? 'text-yellow-500'
                              : 'text-red-500'
                        }`}
                      >
                        {toScore99(entry.score)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Dedication */}
      <div className="mb-6 rounded-xl border border-border bg-card p-4 sm:p-6">
        <h2 className="mb-4 font-semibold text-foreground">
          {t('progress.dedication')}
        </h2>
        <div className="mb-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatBox label={t('progress.totalHours')} value={`${parseFloat(totalHours.toFixed(1))}h`} />
          <StatBox label={t('progress.numSimulations')} value={String(stats.totalSessions)} />
          <StatBox label={t('progress.avgPerSession')} value={stats.totalSessions > 0 ? `${Math.round((totalHours * 60) / stats.totalSessions)} min` : '-'} />
          <StatBox label={t('progress.avgScore')} value={stats.averageScore > 0 ? String(toScore99(stats.averageScore)) : '-'} />
        </div>
        {stats.improvementStreak > 0 && (
          <p className="text-xs text-muted-foreground">
            {t('progress.improvementStreak', { count: stats.improvementStreak })}
          </p>
        )}
      </div>

      {/* Evolution chart */}
      <div>
        <h2 className="mb-4 font-semibold text-foreground">
          {t('progress.scoreEvolution')}
        </h2>
        <ScoreEvolutionChart data={stats.scoreEvolution} />
      </div>
    </div>
  );
}

function StatBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-muted p-3 text-center">
      <p className="text-lg font-bold text-foreground">{value}</p>
      <p className="text-[11px] text-muted-foreground">{label}</p>
    </div>
  );
}
